import { noop } from "lodash";
import { type FC, useCallback, useEffect, useRef } from "react";

import { SwitchEntityTypes } from "enums/switchEntityTypes";
import { ContentTypesEnum, TemplateTypes } from "../../../../enums";
import { type NotifyStepSettings } from "../../../../interfaces";
import { type IObservable } from "../../../../interfaces/IObservable";
import { NotifyStepConfig } from "../../../SystemNotifications/types";
import { type PdfPublishConfirmationParams } from "../types/models";

import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "../../../../components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import ModalTypes from "../../../../components/modal/ModalTypes";
import PublishConfirmationModal, {
  type EntityState,
} from "../../../../components/modal/PublishConfirmationModal/PublishConfirmationModal";
import RevertConfirmationModalTs from "../../../../components/modal/RevertConfirmationModalTs";
import ModalManager from "../../../../utils/ModalManager";
import Observable from "../../../../utils/Observable";

export interface PdfModalsProps {
  publishModal: {
    acceptHandler: (handler: (params: PdfPublishConfirmationParams) => void) => void;
    onConfirm: (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]) => void;
    onClose: () => void;
  };
  deleteModal?: {
    acceptHandler: (handler: (ids: number[], flowsCount?: number, packsCount?: number) => void) => void;
    onConfirm: (ids: number[]) => void;
    onClose: () => void;
  };
  revertModal?: {
    acceptHandler: (handler: () => void) => void;
    onConfirm: () => void;
    userId: number;
    enabled: boolean;
  };
}

export const PdfModalsContainer: FC<PdfModalsProps> = ({ publishModal, deleteModal, revertModal }) => {
  const onTriggerPdfPublishObserver = useRef<IObservable<(onConfirm: () => void, entityState: EntityState) => void>>(
    new Observable(),
  );

  const onTriggerPdfDeleteObserver = useRef<
    IObservable<(onConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>
  >(new Observable());

  const revertModalManager = useRef<ModalManager>(
    new ModalManager(ModalTypes.RevertPdf, revertModal?.onConfirm ?? noop, false, revertModal?.userId),
  );

  const triggerPublish = useCallback(
    (condition: boolean, entityState: EntityState) => {
      return condition
        ? onTriggerPdfPublishObserver.current.notify(noop, entityState)
        : publishModal?.onConfirm(entityState.entityId);
    },
    [publishModal],
  );

  const handlePublishClick = useCallback(
    ({ id, isDraft, flowsCount, hasBeenPublished }: PdfPublishConfirmationParams) => {
      if (isDraft) {
        const entityState = {
          itemsCount: flowsCount,
          entityId: id,
          hasBeenPublished,
          templateType: hasBeenPublished ? TemplateTypes.PdfChanged : TemplateTypes.AddedToPdf,
          notifyConfigType: hasBeenPublished ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default,
          entityType: SwitchEntityTypes.Pdf,
        };

        triggerPublish(hasBeenPublished, entityState);
      }
    },
    [triggerPublish],
  );
  const handleRevertClick = useCallback(() => {
    revertModalManager.current.execute();
  }, []);

  const handleDeleteClick = useCallback(
    (ids: number[], flowsCount?: number, packsCount?: number) => {
      onTriggerPdfDeleteObserver.current.notify(
        () => {
          deleteModal?.onConfirm(ids);
        },
        {
          selectedItemsCount: ids.length,
          flowsCount,
          packsCount,
        },
      );
    },
    [deleteModal],
  );

  useEffect(() => {
    publishModal.acceptHandler(handlePublishClick);
  }, [publishModal, handlePublishClick]);

  useEffect(() => {
    deleteModal?.acceptHandler(handleDeleteClick);
  }, [deleteModal, handleDeleteClick]);

  useEffect(() => {
    revertModal?.acceptHandler(handleRevertClick);
  }, [revertModal, handleRevertClick]);

  return (
    <>
      <PublishConfirmationModal
        onTriggerPublishContentObserver={onTriggerPdfPublishObserver.current}
        onConfirmAction={publishModal?.onConfirm ?? noop}
        contentType="PDF"
        dependencyType="flow"
        onClose={publishModal.onClose ?? noop}
      />

      {deleteModal && (
        <DeleteContentConfirmationModal
          contentType={ContentTypesEnum.PDFs}
          onTriggerRemoveContentObserver={onTriggerPdfDeleteObserver.current}
          onClose={deleteModal.onClose}
        />
      )}
      {revertModal?.enabled && <RevertConfirmationModalTs modalManager={revertModalManager.current} />}
    </>
  );
};
